// src/App.js - Main Application Router
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { isAuthenticated, getUser } from './utils/auth';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import AnonymousReport from './pages/AnonymousReport';
import JournalistDashboard from './pages/JournalistDashboard';
import Profile from './pages/Profile';
import VerifyEmailChange from './pages/VerifyEmailChange';
import ConfirmDelete from './pages/ConfirmDelete';
import Landing from './pages/Landing';
import NotFound from './pages/NotFound';
import AnalyticsTracker from './components/common/AnalyticsTracker';
import AnalyticsConsentBanner from './components/common/AnalyticsConsentBanner';
import './App.css';

// Helper component to redirect authenticated users to appropriate dashboard
const RedirectAuthenticated = ({ children }) => {
  if (isAuthenticated()) {
    const user = getUser();
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'journalist') {
      return <Navigate to="/journalist" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  useEffect(() => {
    document.title = 'Chayyek AI';
  }, []);
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AnalyticsTracker />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={<Landing />}
        />
        {/* Anonymous report: public, no login required (per RFQ) */}
        <Route path="/report" element={<AnonymousReport />} />
        
        {/* Auth Routes - Redirect if already authenticated */}
        <Route
          path="/login"
          element={
            <RedirectAuthenticated>
              <Login />
            </RedirectAuthenticated>
          }
        />
        <Route
          path="/register"
          element={
            <RedirectAuthenticated>
              <Register />
            </RedirectAuthenticated>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RedirectAuthenticated>
              <ForgotPassword />
            </RedirectAuthenticated>
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/verify-email"
          element={<VerifyEmail />}
        />
        <Route
          path="/verify-email-change"
          element={<VerifyEmailChange />}
        />
        <Route
          path="/confirm-delete"
          element={<ConfirmDelete />}
        />
        
        {/* Protected Routes - User Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes - Admin Dashboard */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes - Journalist Dashboard */}
        <Route
          path="/journalist"
          element={
            <ProtectedRoute requireJournalist={true}>
              <JournalistDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Catch-all route for 404 */}
        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>
      <AnalyticsConsentBanner />
    </Router>
  );
}

export default App;
